import { Transaction } from "@common/types/transaction";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useContext } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useOverlays } from "contexts/OverlayContext";
import { useTransactionViewTableMeta } from "@hooks/view/useViewTableMeta";
import { TransactionSplitColumnContent } from "../ListItem/TransactionSplitColumnContent";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { transactionCellAreEqual } from "./transactionCellComparators";
import styles from "./SplitCell.module.scss";

export const SplitCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, any>) => {
        const transaction = row.original;
        const { openOverlay } = useOverlays();
        const { callbacks, options } = useTransactionViewTableMeta(table);
        const { setShownTransactionId } = useContext(
            PaginatedTransactionsActionsContext,
        );

        const { hasTopSibling, hasBottomSibling } =
            callbacks?.calculateSplitSiblings?.(transaction) ?? {
                hasTopSibling: false,
                hasBottomSibling: false,
            };

        const isReadonly = options?.readonly ?? false;

        const handleSplitSave = useCallback(
            (savedTransactions: Transaction[]) => {
                if (!savedTransactions.some((t) => t.id === transaction.id)) {
                    setShownTransactionId(undefined);
                }
            },
            [transaction.id, setShownTransactionId],
        );

        const handleClick = () => {
            openOverlay<"transactionSplit">("transactionSplit", {
                transaction,
                onSplitSave: handleSplitSave,
            });
        };

        return (
            <ViewTableCell captureClicks className={styles.splitCellWrapper}>
                <TransactionSplitColumnContent
                    transaction={transaction}
                    hasTopSibling={hasTopSibling}
                    hasBottomSibling={hasBottomSibling}
                    onClick={isReadonly ? undefined : handleClick}
                />
            </ViewTableCell>
        );
    },
    transactionCellAreEqual,
);
SplitCell.displayName = "SplitCell";
